// Real backend client for the V3 teacher dashboard (claude-teacher-api behind
// the shared HTTP API). Action-based: POST /teacher/api { action, ...params }.
// Sends the ID token (carries custom:Role so the lambda skips its Cognito
// fallback). `auth` = { user, handleAPIResponse } from useAuth(): 401/403
// responses go through handleAPIResponse, which refreshes the token and
// retries; other statuses keep our structured error messages.

// Dev goes same-origin through the CRA proxy in setupProxy.js (the gateway's
// CORS allowlist only has localhost:3000, so direct calls fail on other ports).
// The proxy's pathRewrite is stage-aware (Development by default) — matching
// src/config/api.js, which supplies the built (Production) URL.
import { TEACHER_API_URL } from '../../config/api';
import { teacherViewAsCall, viewingAs } from '../../ViewAs/viewAsApi';

const TEACHER_ENDPOINT = process.env.NODE_ENV === 'development'
  ? '/teacher-api-proxy/teacher/api'
  : TEACHER_API_URL;

export async function teacherApi(auth, action, params = {}, { signal } = {}) {
  // Admin "view as" (2026-09-25): answered by the admin API as the viewed
  // teacher, read only (src/ViewAs/viewAsApi.js).
  if (viewingAs('teacher')) return teacherViewAsCall(auth, action, params, { signal });
  const { user, handleAPIResponse } = auth || {};
  const token = user?.idToken || user?.accessToken;
  if (!token) throw new Error('not signed in');
  const body = { action, ...params };
  let res = await fetch(TEACHER_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
    signal,
  });
  if ((res.status === 401 || res.status === 403) && handleAPIResponse) {
    res = await handleAPIResponse(res, body); // token refresh + retry
  }
  let data = null;
  try { data = await res.json(); } catch { /* non-json error body */ }
  if (!res.ok) {
    const err = new Error(data?.error || `teacher api ${action} failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Home owns a read-only snapshot rather than polling getClasses (which can
// activate scheduled units) or gradebook (which can persist grades).
// `options.field` (2026-10-08): the active unit is a whole-field unit, so the
// snapshot is for that field; domainKey stays a valid key for validation.
export function fetchHomeSnapshot(auth, classID, domainKey, options = {}) {
  const { field, ...rest } = options;
  return teacherApi(auth, 'getHomeSnapshot', { classID, domainKey, ...(field ? { field } : {}) }, rest);
}
const homeListeners = new Set();
export function onHomeChanged(listener) {
  homeListeners.add(listener);
  return () => homeListeners.delete(listener);
}
export function notifyHomeChanged(classID) {
  homeListeners.forEach((fn) => { try { fn(classID); } catch { /* isolated subscriber */ } });
}

// ---- classes (module-cached; the switcher and every page share one fetch) ----
// Caches are scoped to the signed-in user: a sign-out/sign-in with a different
// account must never serve the previous teacher's data.
let classesPromise = null;
let cacheOwner = null;

function ensureCacheOwner(auth) {
  const sub = auth?.user?.username || auth?.user?.idToken || auth?.user?.accessToken || null;
  if (sub !== cacheOwner) {
    cacheOwner = sub;
    classesPromise = null;
    rosterCache.clear();
    stuckCache.clear();
    gradebookCache.clear();
    interveneCache.clear();
  }
}

// API class -> the shape the V3 pages were built against (mockData CLASSES).
function adaptClass(c) {
  return {
    id: c.classID,
    period: c.period || '',
    name: c.name,
    color: c.color || '#3b82f6',
    size: c.size || 0,
    joinCode: c.joinCode,
    school: c.school || null,
    satDate: c.satDate || null,
    // Co-teaching (2026-09-12): a class shared with me by its owner arrives
    // with isOwner:false + the owner's display name. Older responses without
    // the flag are all my own classes.
    isOwner: c.isOwner !== false,
    ownerName: c.ownerName || null,
    startedDomains: c.startedDomains || [],
    // unit: null when no active unit (Home shows the onboarding preview).
    unit: c.unit
      ? {
          subject: c.unit.subject,
          // Field units (2026-10-08): scope 'field', subject, and no domain
          // (each student has their own, see data/unitScope.js). A missing
          // scope is a domain unit, exactly as before.
          ...(c.unit.scope === 'field' ? { scope: 'field' } : {}),
          domain: c.unit.domainKey,
          week: c.unit.week,
          total: c.unit.weeksTotal,
          dueDates: c.unit.dueDates,
          checkpointMode: c.unit.checkpointMode,
          unitSeq: c.unit.unitSeq,
          startDate: c.unit.startDate,
        }
      : null,
    // Waiting-to-go-live unit + per-domain finished counts (2026-08-07 fix:
    // the adapter silently DROPPED scheduledUnit, so the "is scheduled"
    // banner and the picker defaults never saw it). scheduledUnits (2026-08-08)
    // is the whole queue for the schedule calendar; scheduledUnit stays the
    // next one.
    scheduledUnit: c.scheduledUnit || null,
    scheduledUnits: c.scheduledUnits || (c.scheduledUnit ? [c.scheduledUnit] : []),
    finishedUnits: c.finishedUnits || {},
  };
}

export function fetchClasses(auth, { force = false } = {}) {
  ensureCacheOwner(auth);
  if (!classesPromise || force) {
    classesPromise = teacherApi(auth, 'getClasses')
      .then((r) => (r.classes || []).map(adaptClass))
      .catch((e) => { classesPromise = null; throw e; });
  }
  return classesPromise;
}

export function invalidateClasses() { classesPromise = null; classesVersion = nextVersion(); }

// Buffy's class card (claude-sandy-tutor card.mjs, 2026-10-19) is cached on the
// server for 10 minutes. These versions ride every Buffy request
// (sandyApi.js): a write here changes them, so the next turn rebuilds the
// card on whichever server instance answers, and Buffy never answers from the
// class as it was before the teacher's own change. dataVersion is per class
// (classDataChanged); classesVersion moves when a class row changes.
const dataVersions = new Map(); // classID -> version
let classesVersion = '';
let versionSeq = 0;
function nextVersion() { versionSeq += 1; return `${Date.now().toString(36)}.${versionSeq}`; }
export const buffyDataVersion = (classID) => (classID && dataVersions.get(classID)) || '';
export const buffyClassesVersion = () => classesVersion;

// Class-list change subscription: useSelectedClass instances register here so
// a mutation made outside a page (the shell's New class modal) can tell every
// mounted page to re-read the (already invalidated) class cache.
const classListeners = new Set();
export function onClassesChanged(listener) {
  classListeners.add(listener);
  return () => classListeners.delete(listener);
}
export function notifyClassesChanged() {
  classListeners.forEach((fn) => { try { fn(); } catch { /* listener errors stay local */ } });
}

// ---- roster (60s TTL) ----
// Was cached until invalidation, which silently froze Home/Insights minutes
// and integrity flags for the whole tab session. A minute keeps revisits
// instant while staying honestly fresh (the backend data moves in seconds).
const ROSTER_TTL_MS = 60_000;
const rosterCache = new Map(); // classID -> { at, promise }

export async function fetchRoster(auth, classID, { force = false } = {}) {
  ensureCacheOwner(auth);
  const hit = rosterCache.get(classID);
  if (force || !hit || Date.now() - hit.at > ROSTER_TTL_MS) {
    rosterCache.set(classID, {
      at: Date.now(),
      promise: teacherApi(auth, 'getRoster', { classID })
        .then((r) => r.students || [])
        .catch((e) => { rosterCache.delete(classID); throw e; }),
    });
  }
  return rosterCache.get(classID).promise;
}

// ---- stuck list + gradebook (short-TTL cache) ----
// Tab hops and nav-rail hovers hit these constantly mid-class; a 30s window
// makes revisits instant without risking a stale board (mutations invalidate).
const CACHE_TTL_MS = 30_000;
const stuckCache = new Map();     // classID -> { at, promise }
const gradebookCache = new Map(); // classID -> { at, promise }

function cachedCall(cache, classID, make, force, tag) {
  const hit = cache.get(classID);
  // A tagged (synced) request never reuses an untagged (read-only prefetch)
  // entry — otherwise hovering Intervene would suppress the lifecycle sync.
  const usable = hit && Date.now() - hit.at < CACHE_TTL_MS && (!tag || hit.tag === tag);
  if (!force && usable) return hit.promise;
  const promise = make().catch((e) => { cache.delete(classID); throw e; });
  cache.set(classID, { at: Date.now(), promise, tag });
  return promise;
}

export function fetchStuck(auth, classID, { force = false, sync = true } = {}) {
  ensureCacheOwner(auth);
  // sync:false = read-only on the backend (no intervention lifecycle writes) —
  // used by the hover prefetch so a hover never mutates state.
  return cachedCall(stuckCache, classID, () => teacherApi(auth, 'getStuck', { classID, sync }), force, sync ? 'sync' : null);
}
// The month overview carries stuck students too, so it goes with the stuck list.
export function invalidateStuck(classID) { stuckCache.delete(classID); interveneCache.delete(classID); notifyHomeChanged(classID); }

// ---- Intervene month overview (Intervene v2, 2026-08-19) ----
// The whole-roster 30-day categorization (stuck > cheating > clicking >
// tabbing > inactive > on track) with per-student month stats, Focus score,
// and per-flag evidence. Flags move on a month scale, so the short shared TTL
// is plenty; clearMonthFlag invalidates so the flag lifts on the next paint.
const interveneCache = new Map(); // classID -> { at, promise }

export function fetchInterveneOverview(auth, classID, { force = false } = {}) {
  ensureCacheOwner(auth);
  return cachedCall(interveneCache, classID, () => teacherApi(auth, 'getInterveneOverview', { classID }), force, null);
}
export function invalidateInterveneOverview(classID) { interveneCache.delete(classID); }

// "I talked to them" for the month view: restarts that flag's counting window
// (and its Focus-score penalty) from today. Separate from clearLiveFlag —
// Monitor's session clears and these month clears never see each other.
export async function clearMonthFlag(auth, classID, userId, flag) {
  const r = await teacherApi(auth, 'clearMonthFlag', { classID, userId, flag });
  interveneCache.delete(classID);
  notifyHomeChanged(classID);
  return r;
}

export function fetchGradebook(auth, classID, { force = false } = {}) {
  ensureCacheOwner(auth);
  return cachedCall(gradebookCache, classID, () => teacherApi(auth, 'getGradebook', { classID }), force);
}
export function invalidateGradebook(classID) { gradebookCache.delete(classID); }

// Nav-rail hover prefetch: warm the page's data before the click lands.
// Fire-and-forget; errors are swallowed (the page itself will surface them).
export function prefetchFor(auth, label, classID) {
  if (!auth?.user || !classID) return;
  try {
    if (label === 'Intervene') fetchStuck(auth, classID, { sync: false }).catch(() => {});
    else if (label === 'Gradebook') fetchGradebook(auth, classID).catch(() => {});
    else if (label === 'Home' || label === 'Insights' || label === 'Diagnostic') fetchRoster(auth, classID).catch(() => {});
  } catch { /* never let a hover throw */ }
}

export function invalidateRoster(classID) { rosterCache.delete(classID); notifyHomeChanged(classID); }

// One call after anything that changes a class's work, grades, units or stuck
// state (audit F9, 2026-10-19): every per-class cache goes and every open page
// is told once (notifyHomeChanged: Home's snapshot, the homework list,
// Gradebook and Intervene reload), so a change made in a chat or on another
// page shows at once instead of after a 30 s poll or never. `classes: true`
// when the class row changed too (a unit started, finished or moved).
export function classDataChanged(classID, { classes = false } = {}) {
  rosterCache.delete(classID);
  stuckCache.delete(classID);
  interveneCache.delete(classID);
  gradebookCache.delete(classID);
  // Buffy's card rebuilds on the next turn (buffyDataVersion above).
  if (classID) dataVersions.set(classID, nextVersion());
  if (classes) { classesPromise = null; classesVersion = nextVersion(); }
  notifyHomeChanged(classID);
  if (classes) notifyClassesChanged();
}

// ---- SAT diagnostic live view (Diagnostic page) ----
// Talks to the adminDiagnosticControls endpoint the 2.0 Diagnostic Controls
// view already uses (NOT claude-teacher-api): the diagnostic's live state
// lives in DiagnosticTimerSessions / UserProgress, which that lambda owns.
const DIAG_ADMIN_URL = 'https://gyxyzs5421.execute-api.us-east-1.amazonaws.com/Production/adminDiagnosticControls';

export async function diagAdminApi(auth, body) {
  const { user, handleAPIResponse } = auth || {};
  if (!user?.accessToken) throw new Error('not signed in');
  let res = await fetch(DIAG_ADMIN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: user.accessToken },
    body: JSON.stringify(body),
  });
  if ((res.status === 401 || res.status === 403) && handleAPIResponse) {
    res = await handleAPIResponse(res, body);
  }
  let data = null;
  try { data = await res.json(); } catch { /* non-json error body */ }
  if (!res.ok) {
    const err = new Error(data?.error || `diagnostic api ${body.action} failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

// Rich per-module + behavior data (get_monitor_detail, lambda change
// 2026-08-13). Until that deploys, the old lambda answers "Unknown action" —
// fall back to get_class_status and mark the result `limited` so the page can
// say what's missing instead of breaking.
export async function fetchDiagnosticLive(auth, { assignmentId, schoolName, studentIds }) {
  try {
    return await diagAdminApi(auth, {
      action: 'get_monitor_detail',
      assignmentId,
      ...(schoolName ? { schoolName } : {}),
      ...(studentIds?.length ? { studentIds } : {}),
    });
  } catch (e) {
    if (!schoolName) throw e; // get_class_status is school-scoped only
    const r = await diagAdminApi(auth, { action: 'get_class_status', assignmentId, schoolName });
    return { ...r, limited: true };
  }
}

// Reset one live-monitor flag's counters for a student, from this moment
// (Sandy 2026-08-09): "I talked to them" — the flag re-fires only if the
// behavior continues after the clear.
export function clearLiveFlag(auth, classID, userId, flag) {
  return teacherApi(auth, 'clearLiveFlag', { classID, userId, flag });
}

// Teacher diagnostic reset (2026-09-28): one press. When the student is in
// the test the lambda pauses them first and answers 202 { status: 'pausing' };
// keep asking every few seconds until it finishes (200). The roster carries
// diagnostic scores, so it is refetched after.
const RESET_POLL_MS = 5000;
const RESET_MAX_WAIT_MS = 3 * 60 * 1000;
export async function resetStudentDiagnostic(auth, classID, userId, assignmentId, reason, {
  onPausing, pollMs = RESET_POLL_MS, maxWaitMs = RESET_MAX_WAIT_MS,
} = {}) {
  const started = Date.now();
  for (;;) {
    const r = await teacherApi(auth, 'resetDiagnostic', { classID, userId, assignmentId, ...(reason ? { reason } : {}) });
    if (r?.status !== 'pausing') {
      invalidateRoster(classID);
      return r;
    }
    if (onPausing) onPausing(r);
    if (Date.now() - started > maxWaitMs) {
      throw Object.assign(new Error('They are still in the test. It stops at their next check-in, so try Reset again in a minute.'), { code: 'STILL_PAUSING' });
    }
    await new Promise((ok) => setTimeout(ok, pollMs));
  }
}

export async function releaseStudent(auth, classID, userId, mode, domainId) {
  // domainId picks WHICH open intervention to release when the student is
  // stuck in several domains (cross-domain Intervene, Sandy 2026-08-09).
  const r = await teacherApi(auth, 'releaseStudent', { classID, userId, mode, ...(domainId ? { domainId } : {}) });
  // A release changes the stuck list, the month overview AND the roster's
  // per-domain stuck flags (Insights' stuck count and the daily brief read it).
  classDataChanged(classID);
  return r;
}

// ---- unit actions ----
// Send the exact preview dates. Never silently drop an unsupported list and
// let the server create a different four-checkpoint schedule.
function checkedDueDates(dueDates, continuous) {
  if (dueDates == null) return {};
  const countValid = Array.isArray(dueDates) && (continuous ? dueDates.length >= 1 && dueDates.length <= 120 : dueDates.length === 4);
  if (!countValid || !dueDates.every((date, index) => typeof date === 'string' && Number.isFinite(Date.parse(date))
    && (index === 0 || Date.parse(date) > Date.parse(dueDates[index - 1])))) {
    throw Object.assign(new Error('The checkpoint dates are invalid. Review the schedule before starting.'), { status: 400 });
  }
  return { dueDates };
}
// Whole-field units (2026-10-08): `options.field` ('math' | 'reading') sends
// `field` INSTEAD of domainKey; the backend then picks each student's own
// domain. Continuous checkpoints only (the legacy four-slot shape has no
// meaning across domains).
//
// Deploy-order gate: getHomeworkCapabilities answers `fieldUnits: true` once
// the backend has the option on (env FIELD_UNITS_ENABLED); until then the
// pickers hide the whole-field cards, and a field start/schedule answers 503.
export const FIELD_UNITS_OFF_MESSAGE = 'Whole-field units are not turned on yet. Choose one domain for now.';
export function fetchFieldUnitsEnabled(auth, options = {}) {
  return teacherApi(auth, 'getHomeworkCapabilities', {}, options)
    .then((r) => r?.fieldUnits === true)
    .catch(() => false);
}
function fieldUnitError(e, options) {
  if (options.field && e?.status === 503) {
    return Object.assign(new Error(e.data?.error || FIELD_UNITS_OFF_MESSAGE), { status: 503, data: e.data, fieldUnitsOff: true });
  }
  return e;
}
function unitScopeParams(domainKey, options) {
  if (!options.field) return { domainKey };
  if (!['math', 'reading'].includes(options.field)) {
    throw Object.assign(new Error('Choose Math or Reading & Writing for a whole-field unit.'), { status: 400 });
  }
  if (options.checkpointMode !== 'continuous') {
    throw Object.assign(new Error('A whole-field unit needs the continuous checkpoint schedule.'), { status: 400 });
  }
  return { field: options.field };
}
export async function startUnit(auth, classID, domainKey, dueDates, pacingWeeks, options = {}) {
  const r = await teacherApi(auth, 'startUnit', {
    classID, ...unitScopeParams(domainKey, options),
    ...checkedDueDates(dueDates, options.checkpointMode === 'continuous'),
    ...(options.checkpointMode === 'continuous' ? { checkpointMode: 'continuous' } : {}),
    ...(pacingWeeks ? { pacingWeeks } : {}),
  }).catch((e) => { throw fieldUnitError(e, options); });
  classDataChanged(classID, { classes: true });
  return r.unit;
}

// Schedule a unit to go live at an exact date+time (UTC ISO). A time that has
// already passed starts the unit immediately (the backend decides).
export async function scheduleUnit(auth, classID, domainKey, startAt, dueDates, options = {}) {
  const r = await teacherApi(auth, 'scheduleUnit', {
    classID, ...unitScopeParams(domainKey, options), startAt,
    ...checkedDueDates(dueDates, options.checkpointMode === 'continuous'),
    ...(options.checkpointMode === 'continuous' ? { checkpointMode: 'continuous' } : {}),
  }).catch((e) => { throw fieldUnitError(e, options); });
  classDataChanged(classID, { classes: true });
  return r.unit;
}

// Flip a scheduled unit live right now, without waiting for its timer.
export async function startScheduledUnit(auth, classID, unitSeq) {
  const r = await teacherApi(auth, 'startScheduledUnit', { classID, unitSeq });
  classDataChanged(classID, { classes: true });
  return r;
}

export async function cancelScheduledUnit(auth, classID, unitSeq) {
  const r = await teacherApi(auth, 'cancelScheduledUnit', { classID, unitSeq });
  classDataChanged(classID, { classes: true });
  return r;
}

export async function updateDueDates(auth, classID, unitSeq, dueDates) {
  await teacherApi(auth, 'updateDueDates', { classID, unitSeq, dueDates });
  // New due dates move what is late, so grades change too.
  classDataChanged(classID, { classes: true });
}

// Abandon drops the active unit ungraded (the backend deletes any snapshots),
// so every per-class cache is stale afterwards — flush them all.
// Finish the active unit: snapshots every student's grades into permanent rows
// and flips the unit to 'finished'. Always available (Sandy 2026-08-07) — the
// teacher decides when a unit is over, not the due dates.
export async function finishUnit(auth, classID) {
  const r = await teacherApi(auth, 'finishUnit', { classID, wrapUp: { reteachAnswer: null, feedback: null } });
  classDataChanged(classID, { classes: true });
  return r;
}

export async function abandonUnit(auth, classID) {
  const r = await teacherApi(auth, 'abandonUnit', { classID });
  // It invalidated the gradebook twice and never told the pages (audit F9).
  classDataChanged(classID, { classes: true });
  return r;
}

export async function getUnits(auth, classID) {
  return teacherApi(auth, 'getUnits', { classID });
}

export async function updateSettings(auth, classID, settings) {
  return teacherApi(auth, 'updateSettings', { classID, settings });
}

export async function updateClass(auth, classID, fields) {
  const r = await teacherApi(auth, 'updateClass', { classID, ...fields });
  invalidateClasses();
  return r;
}

// ---- weekly class history (Insights trend; real logged activity) ----
export async function getClassHistory(auth, classID, weeks = 8) {
  return teacherApi(auth, 'getClassHistory', { classID, weeks });
}

// ---- Home inbox (2026-10-19, plan P4): items the daily runner wrote ----
// getInbox -> { items, openCount }, newest first, open (or snoozed and due
// again). op: 'dismiss' | 'snooze' | 'act' | 'done'; `with` names the action
// taken for act ('remind', 'schedule_retake', ...); `until` (ISO) for snooze.
export async function fetchInbox(auth, classID, { signal } = {}) {
  return teacherApi(auth, 'getInbox', { classID }, { signal });
}
export async function inboxAction(auth, classID, id, op, { with: taken, until } = {}) {
  return teacherApi(auth, 'inboxAction', { classID, id, op, ...(taken ? { with: taken } : {}), ...(until ? { until } : {}) });
}
// A set scheduled from an inbox item, kept as a link so a later check-in can
// compare before and after (task kind 'set'; day = the set's local open day).
export async function linkScheduledSet(auth, classID, { hwId, day, fromItemId }) {
  return teacherApi(auth, 'putTask', { classID, task: { kind: 'set', day, hwId, ...(fromItemId ? { fromItemId } : {}) } });
}
// Buffy's notes on Home (plan W4-D): the goals and check-ins Buffy saved for
// this class, and Cancel. listGoals -> { goals } (not dropped); listTasks ->
// { tasks } (not dropped). deleteGoal also drops the goal's open check-ins.
export async function listGoals(auth, classID, { signal } = {}) {
  return teacherApi(auth, 'listGoals', { classID }, { signal });
}
export async function listTasks(auth, classID, { signal } = {}) {
  return teacherApi(auth, 'listTasks', { classID }, { signal });
}
export async function deleteGoal(auth, classID, id) {
  return teacherApi(auth, 'deleteGoal', { classID, id });
}
export async function deleteTask(auth, classID, id) {
  return teacherApi(auth, 'deleteTask', { classID, id });
}
// A check-in the teacher set on a page (Assessments "Remind me"): it fires on
// its day like one Buffy saved in chat. day: the local day, YYYY-MM-DD.
export async function scheduleCheckin(auth, classID, { day, text }) {
  return teacherApi(auth, 'putTask', { classID, task: { kind: 'checkin', day, text } });
}
// Buffy Tasks' Coming up and Done (2026-10-21, scratch/buffy-work-2026-10-21
// PLAN.md stage 1). listWatches -> { runnerOn, watches }: the sets and units
// the runner follows, each with the days of its next cards (nudgeOn, reportOn,
// checkOn; none when the runner does not walk this teacher). deleteWatch:
// Stop, no more cards for it. listInboxHistory -> { items }: the cards Buffy
// made in the last 14 days, any status, newest first.
export async function listWatches(auth, classID, { signal } = {}) {
  return teacherApi(auth, 'listWatches', { classID }, { signal });
}
export async function deleteWatch(auth, classID, { kind, targetId }) {
  return teacherApi(auth, 'deleteWatch', { classID, kind, targetId });
}
export async function listInboxHistory(auth, classID, { signal } = {}) {
  return teacherApi(auth, 'listInboxHistory', { classID }, { signal });
}

// ---- in-app nudge: one student (userId) or the whole class (omit userId) ----
export async function sendReminder(auth, classID, userId, message) {
  const params = { classID };
  if (userId) params.userId = userId;
  if (message) params.message = message;
  return teacherApi(auth, 'sendReminder', params);
}

// Reset a student's password to a teacher-chosen 6-digit temp code (2026-08-23).
// Cognito forces the student to set a new password at their next sign-in.
// Returns { ok, email } where email is masked (for "use this account" hints).
export async function resetStudentPassword(auth, classID, userId, code) {
  return teacherApi(auth, 'resetStudentPassword', { classID, userId, code });
}

// ---- CSV re-export for a finished/synced unit ----
export async function exportGrades(auth, classID, unitSeq) {
  return teacherApi(auth, 'exportGrades', { classID, unitSeq });
}

// ---- manual grade overrides (pin / unpin a student's unit grade) ----
// UNIT-FINAL ONLY, by design (Sandy 2026-08-08): per-assignment slot edits
// were built and removed the same day. Works on locked units too — the
// manual unit grade is the tool for correcting a unit after it finished.
export async function setGradeOverride(auth, classID, unitSeq, userId, score) {
  const r = await teacherApi(auth, 'setGradeOverride', { classID, unitSeq, userId, score });
  invalidateGradebook(classID);
  return r;
}
export async function clearGradeOverride(auth, classID, unitSeq, userId) {
  const r = await teacherApi(auth, 'clearGradeOverride', { classID, unitSeq, userId });
  invalidateGradebook(classID);
  return r;
}

// ---- daily-brief: class-wide missed skills (Sandy 2026-08-08) ----
// Aggregated from every recorded miss this unit (not just skill checks).
// No cache: the brief opens once per session, and the questions page is a
// fresh tab.
export async function fetchClassMissedSkills(auth, classID) {
  return teacherApi(auth, 'getClassMissedSkills', { classID });
}
export async function fetchMissedQuestions(auth, classID, skills) {
  return teacherApi(auth, 'getMissedQuestions', { classID, skills });
}
// Skill-name click in the brief: what the skill is + one example question.
export async function fetchSkillInfo(auth, key, band) {
  return teacherApi(auth, 'getSkillInfo', { key, ...(band != null ? { band } : {}) });
}
// Monitor's live-stuck click: the student's last few graded questions with
// their picks and band skills (read-only, no release machinery).
// includeDiagnostic (2026-08-24): Intervene's student detail passes true to
// get the FULL history with diagnostic sessions merged in (kind:
// 'homework' | 'diagnostic', most recent first). Monitor keeps the default.
export async function fetchRecentWork(auth, classID, userId, limit = 5, includeDiagnostic = false) {
  return teacherApi(auth, 'getRecentWork', { classID, userId, limit, ...(includeDiagnostic ? { includeDiagnostic: true } : {}) });
}
// Light probe for the shell's live-session pill — polled, never cached.
export async function fetchSessionStatus(auth, classID) {
  return teacherApi(auth, 'getSessionStatus', { classID });
}

export async function createClass(auth, fields) {
  const r = await teacherApi(auth, 'createClass', fields);
  invalidateClasses();
  return r.class;
}

// ---- roster membership (Manage class modal, 2026-08-05) ----
// The students this teacher can add: everyone at their school when their
// account has one (scope 'school'), else everyone across their own classes
// (scope 'my-classes'). { students: [{ userId, name, classIDs, ... }], scope,
// school }. Uncached; the modal fetches on open. It returned only the list
// (P8 follow-up, 2026-10-19), so the modal never knew the list was the
// school's and never said "Add from your school".
export async function listStudentPool(auth) {
  const r = await teacherApi(auth, 'listStudentPool');
  return { students: r.students || [], scope: r.scope || null, school: r.school || null };
}

// Enroll students into a class. The backend applies the slot-window late-joiner
// rule (new members only get assignments that are not yet due), so no extra
// params. Roster and class list (size) are stale afterwards. Every per-class
// cache goes too, and Buffy's card version moves (BBO-104, 2026-10-29: Buffy
// kept the class from before the add and said it had 0 students), the same as
// removeStudentFromClass.
export async function addStudents(auth, classID, students) {
  const r = await teacherApi(auth, 'addStudents', { classID, students });
  classDataChanged(classID, { classes: true });
  return r; // { added, skipped }
}

// Hard-deletes the enrollment (class data only; the student's own work is
// untouched). Stuck/gradebook still list the student until flushed.
export async function removeStudentFromClass(auth, classID, userId) {
  const r = await teacherApi(auth, 'removeStudent', { classID, userId });
  classDataChanged(classID, { classes: true });
  return r;
}

// Full cascade (enrollments, units, homework, sessions, grades, interventions,
// the class row) — every cache keyed by this class is garbage afterwards.
export async function deleteClass(auth, classID) {
  const r = await teacherApi(auth, 'deleteClass', { classID });
  classDataChanged(classID, { classes: true });
  return r; // { deleted, rows }
}

// ---- co-teachers (2026-09-12) ----
// Per-class grants: the owner adds same-school teachers who then see and run
// the class exactly like the owner (everything but delete and the co-teacher
// list itself). The class list carries isOwner/ownerName per class.
export async function listCoTeachers(auth, classID) {
  const r = await teacherApi(auth, 'listCoTeachers', { classID });
  return { coTeachers: r.coTeachers || [], owner: r.owner || null, isOwner: r.isOwner !== false };
}
// Same-school Teacher accounts the owner can pick from (never includes me).
// { teachers: [...], school } — school null means the owner's account has
// no school set, so there is nothing to pick from.
export async function listSchoolTeachers(auth, classID) {
  const r = await teacherApi(auth, 'listSchoolTeachers', { classID });
  return { teachers: r.teachers || [], school: r.school || null };
}
export async function addCoTeacher(auth, classID, userId) {
  const r = await teacherApi(auth, 'addCoTeacher', { classID, userId });
  return r.coTeacher || null;
}
// Owner removes a co-teacher, or a co-teacher removes themself (leave).
export async function removeCoTeacher(auth, classID, userId) {
  const r = await teacherApi(auth, 'removeCoTeacher', { classID, userId });
  invalidateClasses();
  return r;
}

// ---- live monitor (never cached — Monitor polls this) ----
export async function startSession(auth, classID) {
  return (await teacherApi(auth, 'startSession', { classID })).session;
}
export async function endSession(auth, classID) {
  return teacherApi(auth, 'endSession', { classID });
}
export async function markAbsent(auth, classID, userId) {
  return teacherApi(auth, 'markAbsent', { classID, userId });
}
// Undo an Absent mark for this session (BBO-113, 2026-10-29).
export async function unmarkAbsent(auth, classID, userId) {
  return teacherApi(auth, 'unmarkAbsent', { classID, userId });
}
export async function getLiveState(auth, classID) {
  return teacherApi(auth, 'getLiveState', { classID });
}
